/**
 * @architecture-header 1
 * @layer app
 * @domain workspace
 * @responsibility Compose environment and TanStack Query providers for the authenticated product.
 * @state-owner TanStack Query server cache
 * @allowed-imports React; TanStack Query; app config
 * @forbidden module business logic; host bootstrap
 * @public-api via WorkspaceRoot
 * @side-effects creates one query cache per mounted product
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type PropsWithChildren, useState } from 'react';
import { EnvironmentProvider, type WorkspaceEnvironment } from '../config/environment';
import { SessionProvider } from './SessionProvider';

export function WorkspaceProviders({
  children,
  environment,
}: PropsWithChildren<{ environment: WorkspaceEnvironment }>) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  return (
    <EnvironmentProvider environment={environment}>
      <SessionProvider>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </SessionProvider>
    </EnvironmentProvider>
  );
}
